/**
 * SpendBreakdown — where a team's money went, drawn the same way on both league
 * pages.
 *
 * 🔴 TWO PAGES DRAW A ROOM AND THEY MUST DRAW IT THE SAME WAY. The Sleeper
 * league report (ReportView) and the hand-entered room (LocalRoomReport) are two
 * sources of one object, so this rendering lives here and both import it — the
 * same reasoning RoomRoster's header already records. A second copy would look
 * identical the day it shipped.
 *
 * The collapsed card gets the BAR: a quiet stacked strip in the position colours
 * this site already uses everywhere, with the dollar total at the end. The
 * expansion gets the DETAIL: the same positions read against two yardsticks.
 *
 * 🔴 ONE PAGE, ONE YARDSTICK — and this is the one place the page compares
 * against something outside the room, so it says so in words rather than
 * smuggling it in. The room column and the market column are DIFFERENT
 * ESTIMATORS (median-of-shares here, pooled dollars there), which is exactly
 * the kind of mix the trends work keeps getting bitten by, so the fine print
 * names both. Dollars against dollars throughout: no percentile appears here,
 * because a corpus percentile on this page would give one report two yardsticks.
 */

import { POS_COLORS } from "../../data/players.js";
import { theme } from "../../data/theme.js";
import { C, SZ } from "../Submit/submitStyles.js";
import { money } from "./GroupCard.jsx";
import { barFill, prefersReducedMotion } from "./rateMotion.js";

/**
 * A segment narrower than this cannot carry its own label without the text
 * spilling over its neighbours, so it goes unlabelled and the detail below
 * names it instead. Measured against the bar's own width, so it holds at any
 * viewport.
 */
const LABEL_MIN_SHARE = 0.1;

/**
 * The bar's fill and its label ink, taken as a PAIR off POS_COLORS the way
 * TeamEntryLadder's chips already take them — bg is the solid position colour
 * and text is what stays legible on it.
 */
function posFill(pos) {
  return (POS_COLORS[pos] && POS_COLORS[pos].bg) || C.bg.deep;
}

function posInk(pos) {
  return (POS_COLORS[pos] && POS_COLORS[pos].text) || C.text.primary;
}

/**
 * The compact stacked bar for a collapsed card.
 *
 * Renders nothing at all when there is no breakdown — an unpriced room has no
 * spend bar rather than an empty one.
 *
 * 🔴 `mini` IS A DENSITY, NOT A SECOND BAR. The team list draws this same
 * strip at a thinner height with no dollar total beside it, because the row it
 * sits in already carries the money in words. It is a prop rather than a
 * component so that the segment order, the colour pairing, the label cutoff and
 * the fill animation can only ever be defined once — a "MiniSpendBar" would
 * look identical the day it shipped and drift the week after, which is the
 * exact lesson this file's own header records about the two league pages.
 */
export function SpendBar({ breakdown, testId, mini }) {
  // Hooks run before the early return, so the rule of hooks holds on the
  // unpriced path where this component renders nothing at all.
  const still = prefersReducedMotion();
  if (!breakdown) return null;
  return (
    <div
      data-testid={testId}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        marginTop: mini ? 6 : 8,
      }}
    >
      <div
        style={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          height: mini ? 6 : 10,
          borderRadius: theme.radius.sm,
          overflow: "hidden",
          background: C.bg.deep,
          // The WHOLE bar grows out of its left edge, rather than each segment
          // animating its own width: one transform keeps every segment's
          // proportion exact the whole way and costs one composited layer.
          ...barFill(still),
        }}
        data-testid={testId ? `${testId}-fill` : undefined}
      >
        {breakdown.segments.map((s) => (
          <div
            key={s.pos}
            data-testid={`spend-seg-${s.pos}`}
            data-dollars={s.dollars}
            title={`${s.pos} ${money(s.dollars)}`}
            style={{
              width: `${s.share * 100}%`,
              background: posFill(s.pos),
              color: posInk(s.pos),
              fontSize: 8,
              fontWeight: 800,
              lineHeight: "10px",
              textAlign: "center",
              overflow: "hidden",
              whiteSpace: "nowrap",
            }}
          >
            {/* A 6px strip cannot carry 8px type, so the mini density drops the
                labels rather than clipping them. The colours still read as the
                same four positions they do everywhere else on the site, and the
                row beside it names the dollars. */}
            {!mini && s.share >= LABEL_MIN_SHARE ? s.pos : ""}
          </div>
        ))}
      </div>
      {!mini && (
        <div style={{ fontSize: SZ.micro, color: C.text.secondary, fontWeight: 700, flexShrink: 0 }}>
          {money(breakdown.total)}
        </div>
      )}
    </div>
  );
}

const cell = { padding: "3px 0", textAlign: "right", fontVariantNumeric: "tabular-nums" };
const head = { ...cell, fontSize: SZ.micro, color: C.text.faint, fontWeight: 700 };

/**
 * The expansion's detail: one row per position, three columns.
 *
 * A yardstick with no figure draws a dash rather than a zero — an artifact
 * built before meta.marketPosShares existed, or a league whose budget never
 * arrived, loses the column and says nothing about it.
 */
export function SpendDetail({ breakdown, testId }) {
  if (!breakdown) return null;
  return (
    <div data-testid={testId} style={{ marginTop: 10 }}>
      <div style={{ fontSize: SZ.small, fontWeight: 800, color: C.text.secondary, marginBottom: 4 }}>
        Where the money went
      </div>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: SZ.small }}>
        <thead>
          <tr>
            <th style={{ ...head, textAlign: "left" }}>Position</th>
            <th style={head}>This team</th>
            <th style={head}>This room</th>
            <th style={head}>2026 market</th>
          </tr>
        </thead>
        <tbody>
          {breakdown.rows.map((row) => (
            <tr key={row.pos} data-testid={`spend-row-${row.pos}`}>
              <td
                style={{
                  ...cell,
                  textAlign: "left",
                  fontWeight: 800,
                  color: (POS_COLORS[row.pos] && POS_COLORS[row.pos].border) || C.text.primary,
                }}
              >
                {row.pos}
              </td>
              <td
                data-testid={`spend-team-${row.pos}`}
                style={{ ...cell, fontWeight: 700, color: C.text.primary }}
              >
                {money(row.dollars)}
              </td>
              <td data-testid={`spend-room-${row.pos}`} style={{ ...cell, color: C.text.secondary }}>
                {row.roomDollars == null ? "—" : money(Math.round(row.roomDollars * 10) / 10)}
              </td>
              <td
                data-testid={`spend-market-${row.pos}`}
                style={{ ...cell, color: C.text.secondary }}
              >
                {row.marketDollars == null ? "—" : money(Math.round(row.marketDollars * 10) / 10)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ marginTop: 6, fontSize: SZ.micro, color: C.text.faint, lineHeight: 1.5 }}>
        This team is its own dollars. This room is the median share the teams in this auction spent
        at that position, at your budget. The 2026 market is pooled dollars across every charted real
        auction, at your budget — a different estimator from the room column, which is why both are
        named. Shares use every priced dollar, kickers and defenses included, so the four skill rows
        add up to a little less than the total. K and DEF pool across rooms that roster neither, so
        they read low against a room that does.
      </div>
    </div>
  );
}

/**
 * "Starters $164 (82%) · Bench $36 (18%)" — the other way of cutting the same
 * money.
 *
 * 🔴 ONE DEFINITION, TWO REPORTS, for the reason this file's header already
 * gives about the bar and the detail: the room card and the seat report both
 * draw this sentence, and a second copy would look identical the day it shipped
 * and drift the week after. leagueSpend.js's starterBenchSpend computes it; this
 * draws it; neither page words it for itself.
 *
 * 🔴 THE BENCH PERCENT IS THE COMPLEMENT OF THE STARTER ONE, not its own
 * rounding. The two halves partition what the team spent, so they have to read
 * as a partition: rounding each independently would let a reader add 51 and 50
 * and get 101, which looks like a bug and is unanswerable on the page.
 *
 * 🔴 AND IT MAKES NO MARKET CLAIM. The corpus carries no starter/bench
 * distribution, so this is the team's own split and nothing else. A "vs real
 * rooms" clause needs a corpus recompute first, not a sentence here.
 *
 * Renders nothing when there is no split — see leagueSpend.js for the three
 * separate ways that happens, all of them absent rather than zeroed.
 */
export function StarterSplitLine({ split, testId, style }) {
  if (!split) return null;
  const starterPct = Math.round(split.starterShare * 100);
  return (
    <div
      data-testid={testId}
      style={{ fontSize: SZ.small, color: C.text.secondary, ...style }}
    >
      Starters <b style={{ color: C.text.primary }}>{money(split.starterSpend)}</b> ({starterPct}%) ·
      Bench <b style={{ color: C.text.primary }}>{money(split.benchSpend)}</b> ({100 - starterPct}%)
    </div>
  );
}
